import { Accordion, Avatar, Box, Link, Stack, styled, Theme, Typography } from '@mui/material';
import { CopyToClipboardButton } from '../button/CopyToClipboardButton';

export const SupportAccordion = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey.A400}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: 'none',

  // REMOVE MUI GROUPING BEHAVIOR
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.grey.A400}`,
  },

  '&::before': {
    display: 'none',
  },

  // KEEP RADIUS WHEN EXPANDED
  '&.Mui-expanded': {
    margin: 0,
  },
}));

type SupportAccordionHeader = {
  icon: React.ReactNode;
  color: string;
  title: string;
  subtitle: string;
  theme: Theme;
  gap?: number;
};

export const SupportAccordionHeader = ({ icon, color, title, subtitle, theme, gap = 2 }: SupportAccordionHeader) => {
  return (
    <Stack direction="row" spacing={gap} alignItems="center">
      <Avatar sx={{ bgcolor: color, color: theme.palette.text.primary }}>{icon}</Avatar>
      <Box>
        <Typography fontWeight={500}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );
};

export const SupportSectionTitle = ({ children, title, color }: { title: string; children: React.ReactNode; color: string }) => {
  return (
    <Box>
      <Typography variant="body2" component="span" fontWeight={500} sx={{ color, textTransform: 'uppercase' }}>
        {title}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
};

interface SupportContactRowProps {
  icon: React.ReactNode;
  iconBg: string;
  text: string;
  copyValue?: string;
  href?: string;
  theme: Theme;
  gap?: number;
}

export const SupportContactRow = ({ icon, iconBg, text, copyValue, href, theme, gap = 1 }: SupportContactRowProps) => {
  const textComponent = (
    <Typography
      sx={{
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Stack direction="row" alignItems="center" spacing={gap}>
      <Avatar
        sx={{
          height: 26,
          width: 26,
          bgcolor: iconBg,
          color: theme.palette.text.primary,
          flexShrink: 0,
        }}
      >
        {icon}
      </Avatar>

      {href ? (
        <Link
          href={href}
          underline="none"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            minWidth: 0,
            flexGrow: 1,
          }}
        >
          {textComponent}
        </Link>
      ) : (
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>{textComponent}</Box>
      )}

      {copyValue && <CopyToClipboardButton size="small" value={copyValue} sx={{ flexShrink: 0 }} />}
    </Stack>
  );
};
